import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Fish, ArrowUpRight, ArrowDownRight, RefreshCw, AlertCircle,
  Waves, Gauge, Coins
} from "lucide-react";
import { API } from "../constants";

const REFRESH_MS = 2 * 60 * 1000; // 2 minutos

function formatUSD(value) {
  if (value === null || value === undefined) return "--";
  const abs = Math.abs(value);
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (seconds < 60) return `hace ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `hace ${hours} h`;
}

function FearGreedCard({ data }) {
  if (!data?.value && data?.value !== 0) return null;
  const v = data.value;
  const color = v <= 25 ? "text-red-400" : v <= 45 ? "text-orange-400" : v <= 55 ? "text-yellow-400" : v <= 75 ? "text-lime-400" : "text-emerald-400";
  return (
    <div className="bg-gray-900/50 rounded-lg p-3 flex items-center gap-3">
      <Gauge className={`w-6 h-6 ${color}`} />
      <div>
        <p className="text-xs text-gray-500">Fear & Greed</p>
        <p className={`text-lg font-bold ${color}`}>
          {v} <span className="text-xs font-normal text-gray-400">{data.classification}</span>
        </p>
      </div>
    </div>
  );
}

function StableFlowCard({ data }) {
  if (!data?.net_flow_7d_usd && data?.net_flow_7d_usd !== 0) return null;
  const positive = data.net_flow_7d_usd >= 0;
  return (
    <div className="bg-gray-900/50 rounded-lg p-3 flex items-center gap-3">
      <Coins className={`w-6 h-6 ${positive ? "text-emerald-400" : "text-red-400"}`} />
      <div>
        <p className="text-xs text-gray-500">Stablecoins (7d)</p>
        <p className={`text-lg font-bold ${positive ? "text-emerald-400" : "text-red-400"}`}>
          {positive ? "+" : ""}{formatUSD(data.net_flow_7d_usd)}
          <span className="text-xs font-normal text-gray-400"> {positive ? "emisión" : "quema"}</span>
        </p>
      </div>
    </div>
  );
}

export function WhaleTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("trades"); // 'trades' | 'defi'

  const fetchWhales = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whales/summary`, { timeout: 20000 });
      setData(res.data);
      setError(null);
    } catch (e) {
      console.error("Error cargando whale tracker:", e);
      setError("Whale tracker no disponible temporalmente. Reintentando…");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWhales();
    const interval = setInterval(fetchWhales, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchWhales]);

  const trades = data?.large_trades || [];
  const flows = data?.defi_flows || [];

  return (
    <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-5 h-full flex flex-col" data-testid="whale-tracker">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Fish className="w-5 h-5 text-cyan-400" /> Whale Tracker
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {data?.generated_at && <span>Act. {timeAgo(data.generated_at)}</span>}
          <button onClick={fetchWhales} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors" title="Actualizar">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Contexto de mercado */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <FearGreedCard data={data?.fear_greed} />
        <StableFlowCard data={data?.stablecoin_flows} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTab("trades")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${tab === "trades" ? "bg-cyan-600 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
        >
          <Waves className="w-4 h-4" /> Grandes órdenes ({trades.length})
        </button>
        <button
          onClick={() => setTab("defi")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${tab === "defi" ? "bg-cyan-600 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
        >
          <Fish className="w-4 h-4" /> Flujos DeFi ({flows.length})
        </button>
      </div>

      {loading && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => <div key={i} className="animate-pulse h-12 bg-gray-800/60 rounded-lg" />)}
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && tab === "trades" && (
        <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "480px" }}>
          {trades.length === 0 && <p className="text-gray-500 text-sm">Sin grandes órdenes detectadas en la última ventana.</p>}
          {trades.map((t, idx) => (
            <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-900/50 rounded-lg text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`flex items-center gap-1 font-semibold ${t.side === "buy" ? "text-emerald-400" : "text-red-400"}`}>
                  {t.side === "buy" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {t.side === "buy" ? "COMPRA" : "VENTA"}
                </span>
                <span className="text-white font-medium">{t.symbol}</span>
                <span className="text-gray-500 text-xs">{t.venue}</span>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-white font-semibold">{formatUSD(t.value_usd)}</p>
                <p className="text-gray-500 text-xs">{timeAgo(t.timestamp)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && tab === "defi" && (
        <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "480px" }}>
          {flows.length === 0 && <p className="text-gray-500 text-sm">Sin flujos DeFi relevantes en las últimas 24h.</p>}
          {flows.map((f, idx) => (
            <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-900/50 rounded-lg text-sm">
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{f.protocol}</p>
                <p className="text-gray-500 text-xs">{f.chain} · {f.category} · TVL {formatUSD(f.tvl_usd)}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`font-semibold ${f.direction === "inflow" ? "text-emerald-400" : "text-red-400"}`}>
                  {f.direction === "inflow" ? "+" : ""}{formatUSD(f.flow_1d_usd)}
                </p>
                <p className={`text-xs ${f.change_1d_pct >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {f.change_1d_pct >= 0 ? "+" : ""}{f.change_1d_pct}% 24h
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-gray-600 mt-3 pt-3 border-t border-gray-800/60">
        Fuentes: {(data?.sources || []).join(" · ") || "—"}
      </p>
    </div>
  );
}

export default WhaleTracker;
